import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxfWatchlistV1Fincen314EntryQueryServiceFincen314EntryGet } from '@interxfi/api-client/generated/hooks/ixf-watchlist-v1-fincen314-entry-query-service-fincen314-entry-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxfWatchlistV1Fincen314EntryState } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { FincenEntryEventDrawer, FincenEntryEventSummary } from '../../../../../../../../../../features/watchlist/fincen/entry/fincen-entry-event';
import { getFincenEntryStateListItems } from '../../../../../../../../../../features/watchlist/fincen/entry/fincen-entry-list-items';
import { getFincenEntryDetailPath } from '../../../../../../../../../../features/watchlist/fincen/entry/fincen-entry-routing';
import { WatchlistFincenEntryStatusPill } from '../../../../../../../../../../features/watchlist/fincen/entry/fincen-entry-status-pill';
import { WatchlistAddressesTable } from '../../../../../../../../../../features/watchlist/watchlist-addresses-table';
import { WatchlistElectronicContactsTable } from '../../../../../../../../../../features/watchlist/watchlist-electronic-contacts-table';
import { WatchlistIdentificationsTable } from '../../../../../../../../../../features/watchlist/watchlist-identifications-table';
import { WatchlistSubjectAliasesTable } from '../../../../../../../../../../features/watchlist/watchlist-subject-aliases-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/compliance/_compliance/fincen/entry/_entry/$fincen314EntryId')(
  {
    loader: ({ params }) =>
      ({
        breadcrumb: buildBreadcrumb({
          id: `fincen-entry-${params.fincen314EntryId}`,
          label: 'Entry',
          to: getFincenEntryDetailPath(params.fincen314EntryId),
          suffix: (
            <CopyButton
              content={params.fincen314EntryId}
              label="Copy FinCEN Entry ID"
              tooltip={{ copy: params.fincen314EntryId, copied: 'Copied FinCEN Entry ID' }}
            />
          ),
        }),
      }) satisfies AggregateBreadcrumb,
    head: () => getPageHead('FinCEN Entry'),
    component: FincenEntryDetail,
  },
);

function getMenuItems(fincen314EntryId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: FincenEntryEventDrawer,
        props: { request: { fincen314EntryId } },
      },
    },
    actions: undefined,
  };
}

function getListItems(data: IxfWatchlistV1Fincen314EntryState | undefined) {
  const items = getFincenEntryStateListItems(data);

  return {
    left: [
      items.data.trackingNumber,
      items.data.spec.typeName,
      ...match(items.data.spec)
        .with({ individual: P.not(P.nullish) }, ({ individual }) => [
          individual.value.firstName,
          individual.value.middleName,
          individual.value.lastName,
          individual.value.suffix,
          individual.value.dob,
          individual.value.phones,
        ])
        .with({ organization: P.not(P.nullish) }, ({ organization }) => [
          organization.value.businessName,
          organization.value.dbaName,
          organization.value.incorporatedDate,
          organization.value.phones,
        ])
        .otherwise(() => []),
    ] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.fincen314EntryId, items.fincen314FileId] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load FinCEN entry', 'view this FinCEN entry');

function FincenEntryDetail() {
  const { fincen314EntryId } = Route.useParams();
  const request = { fincen314EntryId } as const;
  const { data, error, isPending } = useIxfWatchlistV1Fincen314EntryQueryServiceFincen314EntryGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.fincen314Entry), [data?.fincen314Entry]);
  const menuItems = useMemo(() => getMenuItems(fincen314EntryId), [fincen314EntryId]);

  const individual = data?.fincen314Entry?.data?.spec?.individual;
  const organization = data?.fincen314Entry?.data?.spec?.organization;

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <WatchlistFincenEntryStatusPill isPending={showPending} size="large" status={data?.fincen314Entry?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <FincenEntryEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
          />
        </VerticalStack>
      </PageLayout.Section>

      {individual ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Aliases</Typography.Level3>
            <WatchlistSubjectAliasesTable aliases={individual.aliases ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {individual ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Addresses</Typography.Level3>
            <WatchlistAddressesTable addresses={individual.addresses ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {individual ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Identifications</Typography.Level3>
            <WatchlistIdentificationsTable identifications={individual.identifications ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {individual ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Electronic Contacts</Typography.Level3>
            <WatchlistElectronicContactsTable electronicContacts={individual.electronicContacts ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {organization ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Addresses</Typography.Level3>
            <WatchlistAddressesTable addresses={organization.addresses ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {organization ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Identifications</Typography.Level3>
            <WatchlistIdentificationsTable identifications={organization.identifications ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}

      {organization ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Electronic Contacts</Typography.Level3>
            <WatchlistElectronicContactsTable electronicContacts={organization.electronicContacts ?? STABLE_EMPTY_ARRAY_REF} loadingRows={2} />
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
